import React from "react";
import { Layers, Clock, Trophy, XCircle } from "lucide-react";
import { formatCurrency, formatPercent } from "./dashboardUtils";
import type { Deal } from "../types";

interface DashboardKpiCardsProps {
  deals: Deal[];
  wonStages: string[];
  lostStages: string[];
  currency?: string;
}

export const DashboardKpiCards: React.FC<DashboardKpiCardsProps> = ({
  deals,
  wonStages,
  lostStages,
  currency = "BRL",
}) => {
  const totalCount = deals.length;
  const totalAmount = deals.reduce(
    (acc, d) => acc + (Number(d.amount) || 0),
    0,
  );

  const wonDeals = deals.filter((d) => {
    if (d.status === "ganho") return true;
    const stage = (d.stage || "").toLowerCase();
    return wonStages.some((s) => s.toLowerCase() === stage);
  });
  const wonCount = wonDeals.length;
  const wonAmount = wonDeals.reduce(
    (acc, d) => acc + (Number(d.amount) || 0),
    0,
  );

  const lostDeals = deals.filter((d) => {
    if (d.status === "perdido") return true;
    const stage = (d.stage || "").toLowerCase();
    return lostStages.some((s) => s.toLowerCase() === stage);
  });
  const lostCount = lostDeals.length;
  const lostAmount = lostDeals.reduce(
    (acc, d) => acc + (Number(d.amount) || 0),
    0,
  );

  const openDeals = deals.filter((d) => {
    const isWon =
      d.status === "ganho" ||
      wonStages.some((s) => s.toLowerCase() === (d.stage || "").toLowerCase());
    const isLost =
      d.status === "perdido" ||
      lostStages.some((s) => s.toLowerCase() === (d.stage || "").toLowerCase());
    return !isWon && !isLost;
  });
  const openCount = openDeals.length;
  const openAmount = openDeals.reduce(
    (acc, d) => acc + (Number(d.amount) || 0),
    0,
  );

  const conversionRate = totalCount > 0 ? (wonCount / totalCount) * 100 : 0;
  const lossRate = totalCount > 0 ? (lostCount / totalCount) * 100 : 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* 1. Todos os Leads */}
      <div className="bg-card border border-border/80 rounded-xl p-4 shadow-xs relative overflow-hidden group hover:border-primary/50 transition-all">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Total de Leads
          </span>
          <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <Layers className="w-4 h-4" />
          </div>
        </div>
        <div className="mt-2.5 flex items-baseline gap-2">
          <span className="text-2xl font-bold text-foreground">
            {totalCount}
          </span>
          <span className="text-xs text-muted-foreground">
            leads cadastrados
          </span>
        </div>
        <div className="mt-2 text-xs font-medium text-muted-foreground/90 border-t border-border/40 pt-2 flex items-center justify-between">
          <span>Volume total:</span>
          <span className="font-semibold text-foreground">
            {formatCurrency(totalAmount, currency)}
          </span>
        </div>
      </div>

      {/* 2. Em Aberto */}
      <div className="bg-card border border-border/80 rounded-xl p-4 shadow-xs relative overflow-hidden group hover:border-blue-500/50 transition-all">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Em Aberto
          </span>
          <div className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center">
            <Clock className="w-4 h-4" />
          </div>
        </div>
        <div className="mt-2.5 flex items-baseline gap-2">
          <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {openCount}
          </span>
          <span className="text-xs text-muted-foreground">
            (
            {formatPercent(totalCount > 0 ? (openCount / totalCount) * 100 : 0)}
            )
          </span>
        </div>
        <div className="mt-2 text-xs font-medium text-muted-foreground/90 border-t border-border/40 pt-2 flex items-center justify-between">
          <span>Em negociação:</span>
          <span className="font-semibold text-foreground">
            {formatCurrency(openAmount, currency)}
          </span>
        </div>
      </div>

      {/* 3. Ganhos */}
      <div className="bg-card border border-border/80 rounded-xl p-4 shadow-xs relative overflow-hidden group hover:border-emerald-500/50 transition-all">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Ganhos
          </span>
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
            <Trophy className="w-4 h-4" />
          </div>
        </div>
        <div className="mt-2.5 flex items-baseline gap-2">
          <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
            {wonCount}
          </span>
          <span className="text-xs font-medium text-emerald-600/90 dark:text-emerald-400/90">
            ({formatPercent(conversionRate)} conv.)
          </span>
        </div>
        <div className="mt-2 text-xs font-medium text-muted-foreground/90 border-t border-border/40 pt-2 flex items-center justify-between">
          <span>Receita gerada:</span>
          <span className="font-semibold text-emerald-600 dark:text-emerald-400">
            {formatCurrency(wonAmount, currency)}
          </span>
        </div>
      </div>

      {/* 4. Perdidos */}
      <div className="bg-card border border-border/80 rounded-xl p-4 shadow-xs relative overflow-hidden group hover:border-rose-500/50 transition-all">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Perdidos
          </span>
          <div className="w-8 h-8 rounded-lg bg-rose-500/10 text-rose-500 flex items-center justify-center">
            <XCircle className="w-4 h-4" />
          </div>
        </div>
        <div className="mt-2.5 flex items-baseline gap-2">
          <span className="text-2xl font-bold text-rose-600 dark:text-rose-400">
            {lostCount}
          </span>
          <span className="text-xs font-medium text-rose-600/90 dark:text-rose-400/90">
            ({formatPercent(lossRate)} perda)
          </span>
        </div>
        <div className="mt-2 text-xs font-medium text-muted-foreground/90 border-t border-border/40 pt-2 flex items-center justify-between">
          <span>Valor perdido:</span>
          <span className="font-semibold text-foreground">
            {formatCurrency(lostAmount, currency)}
          </span>
        </div>
      </div>
    </div>
  );
};
